// ONE ANSWER TO "you have unsaved work", for every way out.
//
// # The six exits, and the three that were guarded
//
// Somebody types half a leave request and leaves. They can do it by Escape, by clicking
// the scrim, by the pane's close button, by a rail link, by browser Back, or by closing
// the tab. The pane asked about its own three; the other three lost the typing silently,
// and nothing on screen looked wrong afterwards — there was simply no request, and the
// person found out a week later when their leave had not been approved.
//
// So the question is asked in ONE place. Not because six copies would be tedious, but
// because six copies drift: the pane's wording, a rail interception's wording and a
// browser dialog would be three different sentences for one decision, and the third of
// them is not even ours to write.
//
// # The browser's dialog is NOT ours and we do not pretend otherwise
//
// `beforeunload` used to show a page-supplied string. It does not any more, in any
// current browser, and the HTML specification tells user agents not to: a page that could
// write that sentence could write a threat. So this listener calls `preventDefault()` and
// passes no text, and the person sees their browser's own generic wording. Anything else
// here is a string nobody will ever read.
//
// It is also attached ONLY WHILE DIRTY. A permanently-registered `beforeunload` disables
// the back/forward cache in every browser that has one, which makes every navigation in
// the product slower to pay for a dialog that would not have appeared.
//
// # The detail pane deliberately does not use this
//
// A surface with nothing to lose that asks anyway is training. Somebody who is asked to
// confirm a discard four times a day, and has never once had anything to discard, will
// dismiss the fifth one without reading it — including the time it mattered.

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { recordAddressesIn } from "../app/records";
import { useDiscardGuardScope } from "./discard-guard-scope";
import { useCurrentEntry } from "./record-link";

export interface DiscardGuard {
  /** True while an in-app navigation is waiting on an answer. */
  readonly asking: boolean;
  /** Let it go, losing the typing. */
  readonly discard: () => void;
  /** Stay where we are. */
  readonly keep: () => void;
}

/**
 * Holds an in-app navigation and the tab-close, while there is something to lose.
 *
 * Returns the state a dialog renders from rather than rendering one, so the wording and
 * the buttons stay with the surface that knows what is being discarded — the pane already
 * owns that dialog and this only tells it when a fourth exit has been taken.
 */
export function useDiscardGuard(dirty: boolean, ownerQuery?: readonly string[]): DiscardGuard {
  const scope = useDiscardGuardScope();
  const [id] = useState(() => Symbol("working draft"));
  const location = useCurrentEntry();
  const queryKeys = JSON.stringify(ownerQuery ?? []);
  const here = useRef(location);
  here.current = location;
  useLayoutEffect(
    () =>
      dirty
        ? scope.register(id, {
            pathname: here.current.pathname,
            records: recordAddressesIn(here.current.state),
            query: Object.fromEntries(
              (JSON.parse(queryKeys) as string[]).map((key) => [
                key,
                new URLSearchParams(here.current.search).getAll(key),
              ]),
            ),
          })
        : undefined,
    [dirty, id, scope.register, queryKeys],
  );
  const asking = scope.blocker.state === "blocked" && scope.owner === id;
  const discard = useCallback(() => {
    if (asking) scope.discard();
  }, [asking, scope.discard]);
  const keep = useCallback(() => {
    if (asking) scope.keep();
  }, [asking, scope.keep]);
  return { asking, discard, keep };
}
